/**
 * Persisted "have they seen the Coverage Beta onboarding tutorial" state.
 * Richer than a plain "1" flag so localStorage alone can answer "how many
 * labelers bail, and where" — `completed: false` with a low `lastStep`
 * means they skipped early rather than finished.
 */
export interface TutorialState {
  seen: boolean;
  completed: boolean;
  lastStep: number;
  /** ISO timestamp of the last write. */
  at: string;
}

const UNSEEN_STATE: TutorialState = {
  seen: false,
  completed: false,
  lastStep: 0,
  at: "",
};

/**
 * Read persisted tutorial state. A missing key, corrupt JSON, an
 * unexpected shape, or `storage` throwing (Safari private mode can throw on
 * `getItem`, not just `setItem`) all read back as "not seen" — this never
 * throws, so a broken storage backend degrades to "show the tutorial"
 * rather than crashing the page.
 */
export function readTutorialState(
  storage: Storage,
  key: string,
): TutorialState {
  try {
    const raw = storage.getItem(key);
    if (!raw) return { ...UNSEEN_STATE };
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as { seen?: unknown }).seen !== "boolean"
    ) {
      return { ...UNSEEN_STATE };
    }
    const p = parsed as Partial<TutorialState>;
    return {
      seen: p.seen === true,
      completed: p.completed === true,
      lastStep: typeof p.lastStep === "number" ? p.lastStep : 0,
      at: typeof p.at === "string" ? p.at : "",
    };
  } catch {
    return { ...UNSEEN_STATE };
  }
}

/**
 * Persist tutorial state on a terminal dismissal (finish, Skip, or Escape —
 * see CoverageBetaTraining). Always marks `seen: true`; `completed` /
 * `lastStep` record how the tour ended. Swallows a throwing `setItem`
 * (Safari private mode, a full quota, a locked-down profile) rather than
 * let a close action crash mid-dismiss — the tour just re-opens next load.
 */
export function writeTutorialState(
  storage: Storage,
  key: string,
  state: Pick<TutorialState, "completed" | "lastStep">,
): void {
  try {
    const value: TutorialState = {
      seen: true,
      completed: state.completed,
      lastStep: state.lastStep,
      at: new Date().toISOString(),
    };
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable — nothing we can do, and nothing that should
    // block the dismissal that triggered this write.
  }
}
